import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { MenuItem } from 'primeng/api';

import { User } from '@shared/models';
import { WMPerm } from '@walmart/constants';

import {
  hasGlobalStoresSubmenu,
  canViewAnySupplierManagement,
  hasManagementReportsSubmenu,
  hasAdminSubmenu,
  hasStoreQuotesSubmenu,
  hasStoreInformationSubmenu
} from '@walmart/utils';
import { IMenuBuilderService } from '@shared/services';
import { SharedPerm } from '@shared/constants';

@Injectable({
  providedIn: 'root'
})
export class MenuBuilderService implements IMenuBuilderService {
  private router = inject(Router);

  private quotesIcon = 'fa-duotone fa-solid fa-clipboard-list-check';

  private homeIcon = 'fa-duotone fa-solid fa-home';

  private orderTrackingMenuItem: MenuItem = {
    label: $localize`Order Tracking`,
    icon: 'fa-duotone fa-solid fa-truck',
    routerLink: '/ordertracking',
    expanded: this.checkActiveState('/ordertracking')
  };

  private dataEvalBaseMenuItem: MenuItem = {
    label: $localize`Data Evaluation`,
    icon: 'fa-duotone fa-solid fa-chart-mixed'
  };

  private fullDataEvalMenuItem: MenuItem = {
    ...this.dataEvalBaseMenuItem,
    items: [
      {
        label: $localize`Supplier`,
        icon: 'fa-duotone fa-solid fa-industry',
        routerLink: '/supplierdataeval',
        routerLinkActiveOptions: { exact: true }
      },
      {
        label: $localize`Consolidator`,
        icon: 'fa-duotone fa-solid fa-warehouse',
        routerLink: '/consolidatordataeval',
        routerLinkActiveOptions: { exact: true }
      }
    ]
  };

  private partnerAdminMenuItem: MenuItem = {
    label: $localize`Partner`,
    icon: 'fa-duotone fa-solid fa-handshake',
    routerLink: '/admin/partners',
    routerLinkActiveOptions: { exact: true }
  };

  private tenantUserAdminMenuItem: MenuItem = {
    label: $localize`User`,
    icon: 'fa-duotone fa-solid fa-user-gear',
    routerLink: '/admin/users',
    routerLinkActiveOptions: { exact: true }
  };
  private itemAdminMenuItem: MenuItem = {
    label: $localize`Item`,
    icon: 'fa-duotone fa-solid fa-box',
    routerLink: '/admin/items',
    routerLinkActiveOptions: { exact: true }
  };
  private assetAdminMenuItem: MenuItem = {
    label: $localize`Asset`,
    icon: 'fa-duotone fa-solid fa-boxes-stacked',
    routerLink: '/admin/assets',
    routerLinkActiveOptions: { exact: true }
  };
  private poAdminMenuItem: MenuItem = {
    label: $localize`PO`,
    icon: 'fa-duotone fa-solid fa-file-invoice',
    routerLink: '/admin/pos',
    routerLinkActiveOptions: { exact: true }
  };

  private adminMenuIcon = 'fa-duotone fa-solid fa-screwdriver-wrench';

  private adminMenuItemBase: MenuItem = {
    label: $localize`Administration`,
    icon: this.adminMenuIcon
  };

  private adminPageMenuItem: MenuItem = {
    ...this.adminMenuItemBase,
    expanded: this.checkActiveState('/admin')
  };

  private partnerAccountAdminMenuItem: MenuItem = {
    ...this.adminMenuItemBase,
    label: $localize`Account Administration`,
    routerLink: '/accountadmin'
  };

  private repReportMenuItem: MenuItem = {
    label: $localize`REP Report`,
    icon: 'fa-duotone fa-solid fa-file-chart-pie',
    routerLink: '/repreport',
    expanded: this.checkActiveState('/repreport')
  };

  private supplierContactsMenuItem: MenuItem = {
    label: $localize`Supplier Contacts`,
    icon: 'fa-duotone fa-solid fa-address-book',
    routerLink: '/suppliercontacts',
    expanded: this.checkActiveState('/suppliercontacts')
  };

  private schedulingBaseMenuItem: MenuItem = {
    label: $localize`GCC/Scheduling`,
    icon: 'fa-duotone fa-solid fa-calendar-alt'
  };

  private supplierVitalsMenuItem: MenuItem = {
    label: $localize`Vitals`,
    icon: 'fa-solid fa-wave-pulse',
    routerLink: '/suppliervitals'
  };

  /**
   * Builds the menu for the current user
   * @param user User who is logged into the system
   * @returns Array of MenuItem to put in the navigation bar for the user
   */
  buildUserMenu(user: User) {
    const menu: MenuItem[] = [];

    //--------------------------------
    // Home Pages
    //--------------------------------

    // [Supplier] Main Menu
    if (user.hasPermission(WMPerm.ViewSupplierHome) && user.hasPermission(SharedPerm.IsSupplier)) {
      menu.push({
        label: $localize`Main Menu`,
        icon: this.homeIcon,
        routerLink: '/supplierhome',
        expanded: this.checkActiveState('/supplierhome')
      });
    }

    // [Walmart - Market Manager] Home
    if (user.hasPermission(WMPerm.ViewMarketManager)) {
      menu.push({
        label: $localize`Home`,
        icon: this.homeIcon,
        routerLink: '/marketmanager',
        expanded: this.checkActiveState('/marketmanager')
      });
    }

    // [Contractor] Home
    if (user.hasPermission(WMPerm.ViewGCHome)) {
      menu.push({
        label: $localize`Home`,
        icon: this.homeIcon,
        routerLink: '/gcchome',
        expanded: this.checkActiveState('/gcchome')
      });
    }

    //--------------------------------
    // Order Tracking
    //--------------------------------

    if (user.hasPermission(WMPerm.ViewOrderTracking)) {
      menu.push(this.orderTrackingMenuItem);
    }

    //--------------------------------
    // Supplier Management
    //--------------------------------

    if (canViewAnySupplierManagement(user)) {
      menu.push(this.buildSupplierManagementMenu(user));
    }

    //--------------------------------
    // Quotes
    //--------------------------------

    // [Walmart] Store Quotes
    if (hasStoreQuotesSubmenu(user)) {
      menu.push(this.buildStoreQuotesMenu(user));
    }

    // [Supplier] Quotes
    if (user.hasPermission(WMPerm.ViewSupplierQuotes)) {
      menu.push({
        label: $localize`Quotes`,
        icon: this.quotesIcon,
        routerLink: '/supplierquotes'
      });
    }

    //--------------------------------
    // Scheduling
    //--------------------------------

    // [Contractor] Store Information
    if (user.hasPermission(WMPerm.ViewStoreProjects) && user.hasPermission(SharedPerm.IsContractor)) {
      menu.push({
        label: $localize`Store Projects`,
        icon: 'fa-duotone fa-solid fa-store',
        routerLink: '/storeinformation'
      });
    } else if (hasStoreInformationSubmenu(user)) {
      menu.push(this.buildStoreInformationMenu(user));
    }

    if (user.hasPermission(WMPerm.ViewGCCScheduling)) {
      menu.push({
        ...this.schedulingBaseMenuItem,
        routerLink: '/gccscheduling'
      });
    }

    // [Contractor] Supplier Management
    if (user.hasPermission(SharedPerm.IsContractor) && !user.hasPermission(SharedPerm.IsInstallationContractor)) {
      menu.push({
        label: $localize`Supplier Management`,
        icon: 'fa-duotone fa-solid fa-people-roof',
        routerLink: '/contractorsuppliermanagement',
        expanded: this.checkActiveState('/contractorsuppliermanagement')
      });
    }

    //--------------------------------
    // Missing PO
    //--------------------------------

    if (user.hasPermission(WMPerm.ViewMissingPO) && user.hasPermission(SharedPerm.IsSupplier)) {
      menu.push({
        label: $localize`Missing PO`,
        icon: 'fa-duotone fa-solid fa-exclamation-triangle',
        routerLink: '/missingpos'
      });
    }

    //--------------------------------
    // Supplier Contacts
    //--------------------------------

    if (user.hasPermission(WMPerm.ViewSupplierContacts)) {
      menu.push(this.supplierContactsMenuItem);
    }

    //--------------------------------
    // Data Evaluation
    //--------------------------------

    // [Walmart] Data Evaluation (has both Supplier and Consolidator permissions)
    if (user.hasPermission(WMPerm.ViewSupplierDataEval) && user.hasPermission(WMPerm.ViewConsolidatorDataEval)) {
      menu.push(this.fullDataEvalMenuItem);
    }
    // [Supplier] Data Evaluation (has only Supplier permission)
    else if (user.hasPermission(WMPerm.ViewSupplierDataEval)) {
      menu.push({
        ...this.dataEvalBaseMenuItem,
        routerLink: '/supplierdataeval'
      });
    }
    // [Consolidator] Data Evaluation (has only Consolidator permission)
    else if (user.hasPermission(WMPerm.ViewConsolidatorDataEval)) {
      menu.push({
        ...this.dataEvalBaseMenuItem,
        routerLink: '/consolidatordataeval'
      });
    }

    //--------------------------------
    // Management Reports
    //--------------------------------

    if (hasManagementReportsSubmenu(user)) {
      menu.push(this.buildManagementReportsMenu(user));
    }

    //--------------------------------
    // Vitals
    //--------------------------------

    if (user.hasPermission(WMPerm.ViewVitals)) {
      menu.push(this.supplierVitalsMenuItem);
    }

    //--------------------------------
    // REP Report
    //--------------------------------

    if (user.hasPermission(WMPerm.ViewRepReport)) {
      menu.push(this.repReportMenuItem);
    }

    //--------------------------------
    // Administration
    //--------------------------------

    // [Walmart] Administration
    if (hasAdminSubmenu(user)) {
      menu.push(this.buildAdminMenu(user));
    } else if (user.hasPermission(WMPerm.ViewSupplierAdmin) || user.hasPermission(WMPerm.ViewContractorAdmin)) {
      menu.push(this.partnerAccountAdminMenuItem);
    }

    // --------------------------------
    // PULSE Training
    //--------------------------------

    const trainingMenu: MenuItem = {
      label: $localize`PULSE Training`,
      icon: 'fa-duotone fa-solid fa-books',
      routerLink: '/training'
    };

    menu.push(trainingMenu);

    return menu;
  }

  private buildSupplierManagementMenu(user: User): MenuItem {
    const menu: MenuItem = {
      label: $localize`Supplier Management`,
      icon: 'fa-duotone fa-solid fa-people-roof'
    };

    // If we don't do this here then typescript will think that items can be undefined
    menu.items = [];

    // If user has more than just the "ViewSupplierMgmt" permission, build the submenu
    // otherwise, just set the routerLink and expanded properties

    if (user.hasPermission(WMPerm.ViewSupplierMgmtAllPOs) || user.hasPermission(WMPerm.ViewSupplierMgmtGlobalStores)) {
      const path = '/suppliermanagement/reports/globalstores';
      menu.items.push({
        label: $localize`Global Stores – All POs`,
        icon: 'fa-duotone fa-solid fa-list-check',
        routerLink: path,
        expanded: this.checkActiveState(path)
      });
    }

    if (user.hasPermission(WMPerm.ViewSupplierMgmtGlobalSuppliers)) {
      const path = '/suppliermanagement/reports/globalsuppliers';
      menu.items.push({
        label: $localize`Global Suppliers`,
        icon: 'fa-duotone fa-solid fa-users-gear',
        routerLink: path,
        expanded: this.checkActiveState(path)
      });
    }

    // TODO: New permission for assets page?
    if (user.hasPermission(WMPerm.ViewSupplierMgmtAllPOs)) {
      const path = '/suppliermanagement/reports/assets';
      menu.items.push({
        label: $localize`Assets`,
        icon: 'fa-duotone fa-solid fa-cubes',
        routerLink: path,
        expanded: this.checkActiveState(path)
      });
    }

    if (user.hasPermission(WMPerm.ViewSupplierMgmtOTIF)) {
      const path = '/suppliermanagement/otif';
      menu.items.push({
        label: $localize`OTIF`,
        icon: 'fa-duotone fa-solid fa-chart-line',
        routerLink: path,
        expanded: this.checkActiveState(path)
      });
    }

    const globalStoresFiltersMenu: MenuItem = {
      label: $localize`Global Stores – Filters`,
      icon: 'fa-duotone fa-solid fa-earth-americas'
    };

    // Building Global Store Filters submenu if user has any fixture or store request or PreCon Projects permissions
    if (hasGlobalStoresSubmenu(user)) {
      globalStoresFiltersMenu.items = [];

      if (user.hasPermission(WMPerm.ViewSupplierMgmtFixtureMajorProjects)) {
        const path = '/suppliermanagement/reports/fixturemp';
        globalStoresFiltersMenu.items.push({
          label: $localize`Fixture - Major Projects`,
          icon: 'fa-duotone fa-solid fa-building',
          routerLink: path,
          expanded: this.checkActiveState(path)
        });
      }

      if (user.hasPermission(WMPerm.ViewSupplierMgmtFixtureSpecialProjects)) {
        const path = '/suppliermanagement/reports/fixturesp';
        globalStoresFiltersMenu.items.push({
          label: $localize`Fixture - Special Projects`,
          icon: 'fa-duotone fa-solid fa-star',
          routerLink: path,
          expanded: this.checkActiveState(path)
        });
      }

      if (user.hasPermission(WMPerm.ViewSupplierMgmtFixtureDistributions)) {
        const path = '/suppliermanagement/reports/fixtured';
        globalStoresFiltersMenu.items.push({
          label: $localize`Fixture - Distributions`,
          icon: 'fa-duotone fa-solid fa-truck-ramp-box',
          routerLink: path,
          expanded: this.checkActiveState(path)
        });
      }

      if (user.hasPermission(WMPerm.ViewSupplierMgmtFixtureReplenishment)) {
        const path = '/suppliermanagement/reports/fixturer';
        globalStoresFiltersMenu.items.push({
          label: $localize`Fixture - Replenishment`,
          icon: 'fa-duotone fa-solid fa-arrows-rotate',
          routerLink: path,
          expanded: this.checkActiveState(path)
        });
      }

      if (user.hasPermission(WMPerm.ViewSupplierMgmtStoreRequests)) {
        const path = '/suppliermanagement/reports/store';
        globalStoresFiltersMenu.items.push({
          label: $localize`Store Requests`,
          icon: 'fa-duotone fa-solid fa-inbox',
          routerLink: path,
          expanded: this.checkActiveState(path)
        });
      }

      if (user.hasPermission(WMPerm.ViewSupplierMgmtPreConProjects)) {
        const path = '/suppliermanagement/reports/preconp';
        globalStoresFiltersMenu.items.push({
          label: $localize`PreCon Projects`,
          icon: 'fa-duotone fa-solid fa-helmet-safety',
          routerLink: path,
          expanded: this.checkActiveState(path)
        });
      }

      if (globalStoresFiltersMenu.items.length > 0) {
        menu.items.push(globalStoresFiltersMenu);
      }
    }

    // If there is only ONE item then we need to move the routerLink and expanded values to the parent menu item and remove
    // the (SINGLE) menu item
    if (menu.items.length === 1) {
      menu.routerLink = menu.items[0].routerLink;
      menu.expanded = menu.items[0].expanded;
      menu.items = undefined;
    }

    return menu;
  }

  /**
   * Builds the list of menu items that appear under the 'Store Quotes' menu
   * @returns List of items to display
   */
  private buildStoreQuotesMenu(user: User): MenuItem {
    const parentMenu: MenuItem = {
      label: $localize`Store Quotes`,
      icon: this.quotesIcon,
      expanded: this.checkActiveState('/storequotes')
    };

    parentMenu.items = [];

    if (user.hasPermission(WMPerm.ViewStoreQuotes)) {
      parentMenu.items.push({
        label: $localize`Quotes`,
        icon: this.quotesIcon,
        routerLink: '/storequotes',
        routerLinkActiveOptions: { exact: true }
      });
    }

    if (user.hasPermission(WMPerm.ViewQuoteAdmin)) {
      parentMenu.items.push({
        label: $localize`Quotes Administration`,
        icon: 'fa-duotone fa-solid fa-user-gear',
        routerLink: '/storequoteadmin',
        routerLinkActiveOptions: { exact: true }
      });
    }

    if (user.hasPermission(WMPerm.ViewSupplyChainReport)) {
      parentMenu.items.push({
        label: $localize`Supply Chain Report`,
        icon: 'fa-duotone fa-solid fa-truck-fast',
        routerLink: '/supplychainreport',
        routerLinkActiveOptions: { exact: true }
      });
    }

    if (user.hasPermission(WMPerm.ViewRevisionReasonsReport)) {
      parentMenu.items.push({
        label: $localize`Revision Reasons Report`,
        icon: 'fa-duotone fa-solid fa-pen-to-square',
        routerLink: '/revisionreasons',
        routerLinkActiveOptions: { exact: true }
      });
    }

    if (user.hasPermission(WMPerm.ViewRejectionReasonsReport)) {
      parentMenu.items.push({
        label: $localize`Rejection Reasons Report`,
        icon: 'fa-duotone fa-solid fa-ban',
        routerLink: '/rejectionreasons',
        routerLinkActiveOptions: { exact: true }
      });
    }

    if (user.hasPermission(WMPerm.ViewProtoReset)) {
      parentMenu.items!.push({
        label: $localize`Proto Reset`,
        icon: 'fa-duotone fa-solid fa-rotate-left',
        routerLink: '/protoreset',
        routerLinkActiveOptions: { exact: true }
      });
    }

    return parentMenu;
  }

  /**
   * Builds the list of menu items that appear under the 'Management Reports' menu
   * @returns List of items to display
   */
  private buildManagementReportsMenu(user: User): MenuItem {
    const mngmtReportsMenu: MenuItem = {
      label: $localize`Management Reports`,
      icon: 'fa-duotone fa-solid fa-file-chart-column'
    };

    mngmtReportsMenu.items = [];

    if (user.hasPermission(WMPerm.ViewStoreProjectOTIF)) {
      mngmtReportsMenu.items.push({
        label: $localize`Store Project OTIF`,
        icon: 'fa-duotone fa-solid fa-chart-line',
        routerLink: '/mgmtreports/otif'
      });
    }

    if (user.hasPermission(WMPerm.ViewQuotesByProjectType)) {
      mngmtReportsMenu.items.push({
        label: $localize`Quotes by Project Type`,
        icon: 'fa-duotone fa-solid fa-clipboard-list',
        routerLink: '/mgmtreports/quotes'
      });
    }

    if (user.hasPermission(WMPerm.ViewSupplierCompliance)) {
      mngmtReportsMenu.items.push({
        label: $localize`Supplier Compliance`,
        icon: 'fa-duotone fa-solid fa-clipboard-check',
        routerLink: '/mgmtreports/suppliercompliance'
      });
    }

    if (user.hasPermission(WMPerm.ViewAssetErrors)) {
      mngmtReportsMenu.items.push({
        label: $localize`Asset Errors`,
        icon: 'fa-duotone fa-solid fa-triangle-exclamation',
        routerLink: '/mgmtreports/asseterrors'
      });
    }

    if (user.hasPermission(WMPerm.ViewPreConQuotes)) {
      mngmtReportsMenu.items.push({
        label: $localize`Pre-Con Quotes`,
        icon: 'fa-duotone fa-solid fa-file-invoice-dollar',
        routerLink: '/mgmtreports/preconquotes'
      });
    }

    return mngmtReportsMenu;
  }

  private buildAdminMenu(user: User): MenuItem {
    const adminMenu: MenuItem = {
      ...this.adminPageMenuItem
    };

    adminMenu.items = [];

    if (user.hasPermission(WMPerm.ViewPartnerAdmin)) {
      adminMenu.items.push(this.partnerAdminMenuItem);
    }

    if (user.hasPermission(WMPerm.ViewUserAdmin)) {
      adminMenu.items.push(this.tenantUserAdminMenuItem);
    }

    if (user.hasPermission(WMPerm.ViewItemAdmin)) {
      adminMenu.items.push(this.itemAdminMenuItem);
    }

    if (user.hasPermission(WMPerm.ViewPOAdmin)) {
      adminMenu.items.push(this.poAdminMenuItem);
    }

    if (user.hasPermission(WMPerm.ViewAssetAdmin)) {
      adminMenu.items.push(this.assetAdminMenuItem);
    }

    return adminMenu;
  }

  private buildStoreInformationMenu(user: User): MenuItem {
    const storeInformationMenu: MenuItem = {
      label: $localize`Store Information`,
      icon: 'fa-duotone fa-solid fa-store'
    };

    storeInformationMenu.items = [];

    if (user.hasPermission(WMPerm.ViewStoreProjects)) {
      storeInformationMenu.items.push({
        label: $localize`Store Projects`,
        icon: 'fa-duotone fa-solid fa-store',
        routerLink: '/storeinformation'
      });
    }

    if (user.hasPermission(WMPerm.ViewGCCLog)) {
      storeInformationMenu.items.push({
        label: $localize`GCC Log`,
        icon: 'fa-duotone fa-solid fa-clipboard-list',
        routerLink: '/gcclog'
      });
    }

    if (user.hasPermission(WMPerm.ViewLeadTimeViolations)) {
      storeInformationMenu.items.push({
        label: $localize`Lead Time Violations`,
        icon: 'fa-duotone fa-solid fa-clock',
        routerLink: '/leadtimeviolations'
      });
    }

    return storeInformationMenu;
  }

  /**
   * Checks to see if the current route is the SAME as a given route
   * @param givenLink The link we want to check against
   * @returns TRUE if the given route is part of the current route.
   */
  private checkActiveState(givenLink: string) {
    return this.router.url.toLowerCase().indexOf(givenLink.toLowerCase()) > -1;
  }
}
